'use client';

// 풋터에서 유일하게 상호작용이 필요한 부분(Related Sites disclosure)만 클라이언트
// 아일랜드로 분리. 풋터 본체(index.tsx)는 서버 컴포넌트로 유지된다.
// - outside-click/Escape 리스너는 열려 있는 동안에만 부착 (SiteChatbot 의 open 게이팅 패턴).
// - role="menu"/"menuitem" 은 화살표 키 내비게이션이 없어 ARIA 계약 위반이므로
//   disclosure 패턴(aria-expanded + aria-controls)으로 대체.
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './styles.module.scss';

type FamilySiteLink = {
  label: string;
  href: string;
};

type FamilySiteGroup = {
  title: string;
  links: FamilySiteLink[];
};

const FAMILY_SITE_GROUPS: FamilySiteGroup[] = [
  {
    title: 'Companies',
    links: [
      { label: 'NC', href: 'https://nc-ai.com' },
      { label: 'NC AI', href: 'https://nc-ai.com/ko/about' },
    ],
  },
  {
    title: 'Brands',
    links: [
      { label: 'VARCO Game AI', href: 'https://game-ai.varco.ai' },
      { label: 'VARCO Media AI', href: 'https://media-ai.varco.ai' },
      { label: 'VAETKI', href: 'https://www.vaetki.ai' },
    ],
  },
];

export function FamilySitesMenu() {
  const t = useTranslations('intro');
  const [isFamilyMenuOpen, setIsFamilyMenuOpen] = useState(false);
  const familyMenuRef = useRef<HTMLDivElement | null>(null);
  const familyMenuId = useId();

  useEffect(() => {
    if (!isFamilyMenuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (
        familyMenuRef.current &&
        !familyMenuRef.current.contains(event.target as Node)
      ) {
        setIsFamilyMenuOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFamilyMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFamilyMenuOpen]);

  return (
    <div ref={familyMenuRef} className={styles.familyWrap}>
      <button
        type="button"
        aria-expanded={isFamilyMenuOpen}
        aria-controls={familyMenuId}
        aria-label={t('footer.familyMenu.toggleAriaLabel')}
        onClick={() => setIsFamilyMenuOpen((value) => !value)}
        className={styles.familyButton}
      >
        Related Sites
        <span className={styles.familyButtonIcon}>+</span>
      </button>

      {isFamilyMenuOpen ? (
        <div id={familyMenuId} className={styles.familyMenu}>
          <ul
            aria-label={t('footer.familyMenu.listAriaLabel')}
            className={styles.familyMenuList}
          >
            {FAMILY_SITE_GROUPS.map((group, index) => (
              <li
                key={group.title}
                className={index > 0 ? styles.familyGroupDivided : undefined}
              >
                <div className={styles.familyGroupHeader}>{group.title}</div>
                <ul className={styles.familyLinkList}>
                  {group.links.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.familyLink}
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
